import React, { useEffect, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Button, P } from "@upsolve/ui";

import useModal from "../modals/useModal";
import trackComponentAction from "../analytics/trackComponentAction";
import trackComponentImpression from "../analytics/trackComponentImpression";
import withGeoLocation from "../maps/withGeoLocation";
import { getCTARenderedCopy, CTATarget, getCTATrackingTarget, getCTAURL } from "./getCTAData";
import { $TSFixMe } from "@upsolve/shared/dist/types";
import { DebtAdvisorModal } from "../DebtAdvisor/DebtAdvisorModal";
import { createPortal } from "react-dom";

const promptTrackingProps = {
  componentName: "DisabledReadingPrompt",
  componentVersion: "3",
};

/**
 * DisabledReadingPrompt
 * - v0: Full width pop-up with simple question and keep reading button
 * - v1: Added link to bankruptcy myths
 * - v2: ???
 * - v3: Modal support
 *
 * @component
 * @version 3
 */
const DisabledReadingPrompt = (props: $TSFixMe) => {
  const [visible, setVisible] = useState(false);
  const ctaTarget: CTATarget = props.ctaTarget || CTATarget.SCREENER;
  const modal = useModal();

  const CTACopy: { [key in CTATarget]: { prompt: React.ReactNode; buttonText: string; modal?: any } } = {
    [CTATarget.AJM]: {
      prompt:
        "If you're a New York resident and want to receive free legal advice on your debt collection lawsuit, we can help!",
      buttonText: "Get Free Help",
    },
    [CTATarget.SCREENER]: {
      prompt: "Considering bankruptcy? We have a free tool to help you!",
      buttonText: "Go to Free Bankruptcy Tool",
    },
    [CTATarget.STUDENT_LOANS]: {
      prompt: "Want to erase your student loan debt? Our free tool can help!",
      buttonText: "Go to Free Student Loan Tool",
    },
    [CTATarget.DEBT_TRIAGE]: {
      prompt: getCTARenderedCopy(ctaTarget).description,
      buttonText: getCTARenderedCopy(ctaTarget).buttonText,
      modal: <DebtAdvisorModal onHide={() => modal.setIsOpen(false)} trackingPageContext={props.trackingPageContext} />,
    },
  };

  // On Mount
  useEffect(() => {
    const timeLastShown = localStorage.getItem("Upsolve.readingPrompt")
      ? Number(localStorage.getItem("Upsolve.readingPrompt"))
      : null;
    const canShow = !timeLastShown || new Date().getTime() - 1000 * 60 * 10 > timeLastShown;
    if (props.isAllowedToRender() && canShow) {
      trackComponentImpression({
        ...promptTrackingProps,
        pageContext: props.trackingPageContext,
      });
      localStorage.setItem("Upsolve.readingPrompt", new Date().getTime().toString());
      setVisible(true);
    }
  }, [props.isAllowedToRender()]);

  const handleClick = () => {
    trackComponentAction({
      ...promptTrackingProps,
      actionId: `disabledReadingPrompt.${getCTATrackingTarget(ctaTarget)}`,
      actionMethod: "click",
      pageContext: props.trackingPageContext,
    });
    const targetUrl = getCTAURL(ctaTarget);

    if (CTACopy[ctaTarget] && CTACopy[ctaTarget].modal) {
      // Open modal
      modal.setIsOpen(true, CTACopy[ctaTarget].modal);
    } else if (targetUrl) {
      // Navigate to href
      window.location.href = targetUrl;
    }
  };
  return (
    <>
      {visible && ( // Render only if visible and modal button is not clicked
        <StyledDisabledReadingPrompt>
          <div>
            <P>{CTACopy[ctaTarget].prompt}</P>
            <div className="buttons">
              <Button size="lg" onClick={handleClick}>
                {CTACopy[ctaTarget].buttonText}
              </Button>
              <Button
                size="lg"
                inverted
                onClick={() => {
                  setVisible(false);
                  if (props.onClose) props.onClose();
                }}
              >
                Keep Reading
              </Button>
            </div>
            <GlobalStyledDisabledReadingPrompt />
          </div>
        </StyledDisabledReadingPrompt>
      )}
      {modal.isOpen && createPortal(modal.component, document.body)}
    </>
  );
};

const StyledDisabledReadingPrompt = styled.div`
  position: fixed;
  z-index: 60;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  padding: 0.5em 1em 6em;
  border-top: 2px solid ${(props) => props.theme.colors.white[100]};
  background: linear-gradient(0deg, ${(props) => props.theme.colors.white[500]}, white);
  box-shadow: 0 0 140px -5px rgba(0, 0, 0, 0.5);
  text-align: center;
  & > p {
    font-weight: 500;
    font-size: 18px;
    max-width: 640px;
    text-align: center;
    margin: 0 auto;
  }
  .buttons {
    width: 400px;
    max-width: 100%;
    margin: 0 auto;
    padding: 0;
    & > a,
    & > button {
      width: 100%;
      margin-bottom: 0.5em;
      font-size: 18px;
      font-weight: 700;
    }
    & > p {
      text-align: center;
      text-decoration: underline;
      font-size: 18px;
      &:hover {
        cursor: pointer;
      }
    }
  }
`;

const GlobalStyledDisabledReadingPrompt = createGlobalStyle`
  .content-body__container, .article__sidebar {
    filter: blur(4px);
    opacity: 0.7;
    pointer-events: none;
  }
`;

export default withGeoLocation(DisabledReadingPrompt);
